import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface UnreadComment {
  id: string;
  story_id: string;
  story_title: string;
  content: string;
  created_at: string;
  commenter_name: string;
  commenter_id: string;
}

export function useUnreadComments(userId: string | null) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadComments, setUnreadComments] = useState<UnreadComment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchUnreadComments = async () => {
    if (!userId) {
      setUnreadCount(0);
      setUnreadComments([]);
      setLoading(false);
      return;
    }

    try {
      // Get user's last checked timestamp
      const { data: profile } = await supabase
        .from('profiles')
        .select('last_checked_comments_at')
        .eq('id', userId)
        .maybeSingle();

      const lastChecked = profile?.last_checked_comments_at || new Date(0).toISOString();

      // Get all comments on user's stories that are newer than last checked
      const { data: comments, error } = await supabase
        .from('comments')
        .select(`
          id,
          content,
          created_at,
          story_id,
          author_id,
          stories!inner(title, author_id),
          profiles!comments_author_id_fkey(display_name)
        `)
        .eq('stories.author_id', userId)
        .neq('author_id', userId)
        .gt('created_at', lastChecked)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedComments: UnreadComment[] = (comments || []).map((comment: any) => ({
        id: comment.id,
        story_id: comment.story_id,
        story_title: comment.stories.title,
        content: comment.content,
        created_at: comment.created_at,
        commenter_name: comment.profiles?.display_name || 'Anonymous',
        commenter_id: comment.author_id,
      }));

      setUnreadComments(formattedComments);
      setUnreadCount(formattedComments.length);
    } catch (error) {
      console.error('Error fetching unread comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    if (!userId) return;

    try {
      await supabase
        .from('profiles')
        .update({ last_checked_comments_at: new Date().toISOString() })
        .eq('id', userId);

      setUnreadCount(0);
      setUnreadComments([]);
    } catch (error) {
      console.error('Error marking comments as read:', error);
    }
  };

  useEffect(() => {
    fetchUnreadComments();

    // Set up real-time subscription for new comments
    const channel = supabase
      .channel('unread-comments')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'comments',
        },
        () => {
          fetchUnreadComments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  return { unreadCount, unreadComments, loading, markAsRead, refresh: fetchUnreadComments };
}